import React, { useState, useMemo, useEffect } from "react";
import ApiService from "../../services/ApiService";
import { formatDatestring, randomString } from "../../utils";
import { toast } from "react-hot-toast";
import { AnimatePresence } from "framer-motion";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import Footer from "../../Base/Footer";

export default function Request({ event }) {
  const [isShown, setIsShown] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [ref, inView] = useInView({ triggerOnce: true });

  // Partner meetings UI state
  const [companiesExpanded, setCompaniesExpanded] = useState(false);
  const [showAllCompanies, setShowAllCompanies] = useState(false); // kept (not used now, as we always show all)
  const [companySearch, setCompanySearch] = useState(""); // kept (UI commented out below)

  const [form, setForm] = useState({
    options: [],
    companyOptions: [], // array of companyIds
    selectedOption: null, // used for "Meet our portfolio companies" being checked even with 0 companies selected
    firstName: "",
    lastName: "",
    company: "",
    jobTitle: "",
    email: "",
    city: "",
    country: "",
    businessGoals: "",
    referredBy: "",
    partners: [],
  });

  const onChangeInput = (input) => (e) => {
    setForm((prev) => ({ ...prev, [input]: e.target.value }));
  };

  const updateForm = (data) => {
    setForm((prev) => ({ ...prev, ...data }));
  };

  // --- Option IDs ---
  const OPTION_MEET_PORTFOLIO = "fcc4f3db";
  const OPTION_WELCOME_LUNCH = "862f3971";
  const OPTION_WELCOME_DRINKS = "4748672c";
  const OPTION_SUMMIT = "4d0bf3e1";

  // Hooks must run before any early return
  const orderedOptions = useMemo(() => {
    const opts = Array.isArray(event?.options) ? event.options : [];
    if (!opts.length) return [];

    const priority = [
      OPTION_MEET_PORTFOLIO,
      OPTION_WELCOME_LUNCH,
      OPTION_WELCOME_DRINKS,
      OPTION_SUMMIT,
    ];

    const pinned = [];
    const rest = [];

    for (const o of opts) {
      if (priority.includes(o.optionId)) pinned.push(o);
      else rest.push(o);
    }

    pinned.sort(
      (a, b) => priority.indexOf(a.optionId) - priority.indexOf(b.optionId)
    );

    return [...pinned, ...rest];
  }, [event]);

  // Keep portfolio option selection in sync with company selections:
  // - If any companies selected, ensure portfolio optionId is included.
  // - If none selected, only remove portfolio optionId if the user has NOT explicitly checked it (selectedOption).
  useEffect(() => {
    const selectedCompanies = Array.isArray(form.companyOptions)
      ? form.companyOptions
      : [];
    const hasCompanies = selectedCompanies.length > 0;

    const options = Array.isArray(form.options) ? form.options : [];
    const hasPortfolioOption = options.includes(OPTION_MEET_PORTFOLIO);

    if (hasCompanies && !hasPortfolioOption) {
      updateForm({ options: [...options, OPTION_MEET_PORTFOLIO] });
      return;
    }

    if (!hasCompanies && hasPortfolioOption) {
      // Keep it ON if user explicitly checked portfolio option
      const userExplicitlyChecked = form.selectedOption === OPTION_MEET_PORTFOLIO;
      if (!userExplicitlyChecked) {
        updateForm({
          options: options.filter((id) => id !== OPTION_MEET_PORTFOLIO),
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.companyOptions, form.selectedOption]);

  const clearForm = () => {
    setForm({
      options: [],
      companyOptions: [],
      selectedOption: null,
      firstName: "",
      lastName: "",
      company: "",
      jobTitle: "",
      email: "",
      city: "",
      country: "",
      businessGoals: "",
      referredBy: "",
      partners: [],
    });
    setCompaniesExpanded(false);
    setShowAllCompanies(false);
    setCompanySearch("");
  };

  const submitForm = async () => {
    if (!formValid) {
      console.log(`submitForm() - form is invalid`, form);
      return;
    }

    // preserve your existing payload shape
    const payload = {
      ...form,
      eventId: event.eventId,
      optionRequests: form.options,
      // companyOptions already present
    };

    console.log(`submitForm()`, payload);

    try {
      const api = new ApiService();
      const response = await api.post(`landmark/events/submitGlobalEventRsvp`, {
        form: payload,
      });

      if (response && response.success) {
        setTimeout(() => {
          setIsShown(true);
          setSubmitError(false);
          clearForm();
        }, 50);
      } else {
        throw new Error("Error submitting form");
      }
    } catch (error) {
      setIsShown(false);
      setSubmitError(true);
      console.error("Error submitting form", error);
      toast.error("An error occurred when submitting your response.");
    }
  };

  if (!event || !event.eventId) return null;

  let formValid = false;
  let formErrorMsg = ``;

  const validateForm = () => {
    const { options, firstName, lastName, company, jobTitle, email } = form;

    if (!options.length) {
      formErrorMsg = "Please select at least one session";
      return false;
    }
    if (!firstName.length) {
      formErrorMsg = "Please enter your first name";
      return false;
    }
    if (!lastName.length) {
      formErrorMsg = "Please enter your last name";
      return false;
    }
    if (!company.length) {
      formErrorMsg = "Please enter your company";
      return false;
    }
    if (!jobTitle.length) {
      formErrorMsg = "Please enter your job title";
      return false;
    }
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    if (!email.length || !emailRegex.test(email)) {
      formErrorMsg = "Please enter a valid email address";
      return false;
    }
    return true;
  };

  formValid = validateForm();
  const submitDisabledClass = formValid ? "" : "disabled";

  return (
    <>
      <div className="grey-bg-section">
        <div className="options-section-title text-center">
          Landmark RSA Week Experiences (San Francisco)
        </div>

        {/* Top event options cards (portfolio option must be first) */}
        <div className="options-list cap-width slightly-wider">
          {orderedOptions && orderedOptions.length
            ? orderedOptions.map((item, index) => (
                <RequestEventOption
                  itemData={item}
                  key={`${item.optionId}-${index}`}
                  updateForm={updateForm}
                  form={form}
                  event={event}
                  optionIds={{
                    OPTION_MEET_PORTFOLIO,
                    OPTION_WELCOME_LUNCH,
                    OPTION_WELCOME_DRINKS,
                    OPTION_SUMMIT,
                  }}
                  onRequestCompanies={() => {
                    setCompaniesExpanded(true);
                    setTimeout(() => {
                      const el = document.getElementById("apply");
                      if (el)
                        el.scrollIntoView({
                          behavior: "smooth",
                          block: "start",
                        });
                    }, 80);
                  }}
                />
              ))
            : null}
        </div>
      </div>

      <div className="cap-width slightly-wider">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 0 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1.4, ease: "easeOut" }}
        >
          <div className="form-container" id="apply">
            <div className="requestForm">
              <h3>REQUEST AN INVITATION</h3>

              {/* Session checkbox rows (same options, in same order) */}
              {orderedOptions && orderedOptions.length
                ? orderedOptions.map((item, index) => (
                    <RequestEventOption
                      checkboxMode={true}
                      itemData={item}
                      key={`cb-${item.optionId}-${index}`}
                      updateForm={updateForm}
                      form={form}
                      event={event}
                      optionIds={{
                        OPTION_MEET_PORTFOLIO,
                        OPTION_WELCOME_LUNCH,
                        OPTION_WELCOME_DRINKS,
                        OPTION_SUMMIT,
                      }}
                      onPortfolioCheckboxOpenCompanies={() => {
                        setCompaniesExpanded(true);
                        setTimeout(() => {
                          const el = document.querySelector(
                            ".rsa-company-section"
                          );
                          if (el)
                            el.scrollIntoView({
                              behavior: "smooth",
                              block: "start",
                            });
                        }, 80);
                      }}
                    />
                  ))
                : null}

              {/* Company selection UI */}
              <CompanySelection
                event={event}
                form={form}
                updateForm={updateForm}
                companiesExpanded={companiesExpanded}
                setCompaniesExpanded={(v) => {
                  setCompaniesExpanded(v);
                  if (!v) {
                    setShowAllCompanies(false);
                    setCompanySearch("");
                  }
                }}
                showAllCompanies={showAllCompanies}
                setShowAllCompanies={setShowAllCompanies}
                companySearch={companySearch}
                setCompanySearch={setCompanySearch}
                portfolioOptionId={OPTION_MEET_PORTFOLIO}
              />

              <h3 style={{ marginTop: "40px" }}>Your Information</h3>

              <div className="requestForm__inputs">
                <label className="input__outer sm">
                  <h5>First Name</h5>
                  <div className="input">
                    <input
                      type="name"
                      value={form.firstName}
                      onChange={onChangeInput("firstName")}
                    />
                  </div>
                </label>

                <label className="input__outer sm">
                  <h5>Last Name</h5>
                  <div className="input">
                    <input
                      type="text"
                      value={form.lastName}
                      onChange={onChangeInput("lastName")}
                    />
                  </div>
                </label>

                <label className="input__outer sm">
                  <h5>Company</h5>
                  <div className="input">
                    <input
                      type="text"
                      value={form.company}
                      onChange={onChangeInput("company")}
                    />
                  </div>
                </label>

                <label className="input__outer sm">
                  <h5>Job title</h5>
                  <div className="input">
                    <input
                      type="text"
                      value={form.jobTitle}
                      onChange={onChangeInput("jobTitle")}
                    />
                  </div>
                </label>

                <label className="input__outer sm">
                  <h5>Email</h5>
                  <div className="input">
                    <input
                      type="email"
                      value={form.email}
                      onChange={onChangeInput("email")}
                    />
                  </div>
                </label>

                <label className="input__outer sm">
                  <h5>Referred By (optional)</h5>
                  <div className="input">
                    <input
                      type="text"
                      value={form.referredBy}
                      onChange={onChangeInput("referredBy")}
                    />
                  </div>
                </label>

                <label className="input__outer">
                  <h5>Tell us about your business goals (optional)</h5>
                  <div className="input">
                    <input
                      type="text"
                      value={form.businessGoals}
                      onChange={onChangeInput("businessGoals")}
                    />
                  </div>
                </label>
              </div>

              <div className="requestForm submit">
                {!isShown && (
                  <div className="submit-wrapper">
                    <button
                      type="submit"
                      onClick={submitForm}
                      className={`button pink-btn ${submitDisabledClass}`}
                    >
                      SUBMIT
                    </button>
                    {formErrorMsg.length ? (
                      <div className="form-invalid-msg text-center">
                        {formErrorMsg}
                      </div>
                    ) : null}
                  </div>
                )}
              </div>

              <AnimatePresence>
                {isShown && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <div className="submit-success-msg">
                      Thank you — your request has been received.
                      <br />
                      Our team will review and follow up with confirmation and
                      logistic details if we’re able to accommodate you.
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <AnimatePresence>
                {submitError && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <div className="submit-success-msg">
                      There was an error when submitting your form. <br />
                      Please reach out to <b>scui@landmarkventures.com</b> and
                      the team will help log your request.
                      <br />
                      Sorry for the inconvenience.
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      </div>

      <div className="footer-section" style={{ marginTop: "70px" }}></div>
      <div className="ed-footer">
        <Footer alternate={true} />
      </div>
    </>
  );
}

/**
 * Company selection UI for the form section.
 * - stores selected companyIds in form.companyOptions
 * - does NOT require companies to be selected to keep the portfolio option checked
 * - companies always shown (no truncation / no "show all" necessary)
 * - search kept in code but UI commented out
 */
const CompanySelection = ({
  event,
  form,
  updateForm,
  companiesExpanded,
  setCompaniesExpanded,
  companySearch,
  setCompanySearch,
  portfolioOptionId,
}) => {
  // Stable company list
  const allCompanies = useMemo(() => {
    return Array.isArray(event?.companyOptions) ? event.companyOptions : [];
  }, [event?.companyOptions]);

  const selected = Array.isArray(form.companyOptions) ? form.companyOptions : [];
  const selectedCount = selected.length;

  const toggleCompany = (companyId) => {
    const isSelected = selected.includes(companyId);
    const next = isSelected
      ? selected.filter((id) => id !== companyId)
      : [...selected, companyId];

    // If user selects any company, ensure portfolio option is also selected
    const opts = Array.isArray(form.options) ? form.options : [];
    const hasPortfolio = opts.includes(portfolioOptionId);

    const nextOpts =
      next.length > 0 && !hasPortfolio ? [...opts, portfolioOptionId] : opts;

    updateForm({
      companyOptions: next,
      options: nextOpts,
      // do not force selectedOption on/off here; checkbox can be checked independently
    });
  };

  const filteredCompanies = useMemo(() => {
    const q = (companySearch || "").trim().toLowerCase();
    if (!q) return allCompanies;

    return allCompanies.filter((c) => {
      const haystack = [
        c.companyName,
        c.companySummary,
        c.companyDescription,
        c.companyWebsite,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(q);
    });
  }, [allCompanies, companySearch]);

  if (!allCompanies.length) return null;

  const listToShow = filteredCompanies;

  return (
    <div className="rsa-company-section">
      <div
        className="rsa-company-toggle"
        onClick={() => setCompaniesExpanded(!companiesExpanded)}
        role="button"
        tabIndex={0}
      >
        <div className="rsa-company-toggle-left">
          <div className="rsa-company-title">
            Request meetings with portfolio companies
          </div>
          <div className="rsa-company-subtitle">
            Select one or more companies and we’ll aim to facilitate introductions
            during RSA week.
          </div>
        </div>

        <div className="rsa-company-toggle-right">
          <div className="rsa-company-selected-pill-slot">
            <div
              className={`rsa-company-selected-pill ${
                selectedCount ? "is-visible" : "is-hidden"
              }`}
            >
              {selectedCount ? `${selectedCount} selected` : "0 selected"}
            </div>
          </div>

          <div className="rsa-company-toggle-cta">
            {companiesExpanded ? "Hide" : `View companies (${allCompanies.length})`}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {companiesExpanded && (
          <motion.div
            className="rsa-company-panel"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
          >
            <div className="rsa-company-panel-inner">
              <div className="rsa-company-controls">
                {/* Search UI intentionally disabled (kept for later) */}
                {/* <div className="rsa-company-search">
                  <input
                    type="text"
                    placeholder="Search companies (name, focus area, keywords)"
                    value={companySearch}
                    onChange={(e) => setCompanySearch(e.target.value)}
                  />
                </div> */}

                <div className="rsa-company-actions">
                  {selectedCount ? (
                    <button
                      type="button"
                      className="rsa-company-link-btn"
                      onClick={() => {
                        const opts = Array.isArray(form.options) ? form.options : [];
                        const userExplicitlyChecked =
                          form.selectedOption === portfolioOptionId;

                        updateForm({
                          companyOptions: [],
                          options: userExplicitlyChecked
                            ? opts
                            : opts.filter((id) => id !== portfolioOptionId),
                        });

                        // optional: clear any search string when clearing
                        setCompanySearch("");
                      }}
                    >
                      Clear selection
                    </button>
                  ) : null}
                </div>
              </div>

              <div className="rsa-company-list">
                {listToShow.map((c) => {
                  const isSelected = selected.includes(c.companyId);

                  return (
                    <div
                      key={c.companyId}
                      className={`rsa-company-item ${isSelected ? "selected" : ""}`}
                      onClick={() => toggleCompany(c.companyId)}
                      role="button"
                      tabIndex={0}
                    >
                      <div className="rsa-company-item-row">
                        <div className="rsa-company-checkbox">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleCompany(c.companyId)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <span className="rsa-company-checkbox-ui">
                            <img
                              src={
                                process.env.PUBLIC_URL +
                                "/images/icons/tick-white.svg"
                              }
                              alt=""
                            />
                          </span>
                        </div>

                        <div className="rsa-company-content">
                          <div className="rsa-company-header">
                            {c.companyWebsite ? (
                              <a
                                className="rsa-company-title-link"
                                href={c.companyWebsite}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div className="rsa-company-name-line">
                                  <div className="rsa-company-name">
                                    {c.companyName}
                                  </div>

                                  {c.companySummary ? (
                                    <div className="rsa-company-summary-inline">
                                      <span className="rsa-company-summary-sep">
                                        —
                                      </span>
                                      <span>{c.companySummary}</span>
                                    </div>
                                  ) : null}
                                </div>
                              </a>
                            ) : (
                              <div className="rsa-company-name-line">
                                <div className="rsa-company-name">
                                  {c.companyName}
                                </div>

                                {c.companySummary ? (
                                  <div className="rsa-company-summary-inline">
                                    <span className="rsa-company-summary-sep">
                                      —
                                    </span>
                                    <span>{c.companySummary}</span>
                                  </div>
                                ) : null}
                              </div>
                            )}
                          </div>

                          {c.companyDescription ? (
                            <div className="rsa-company-description">
                              {c.companyDescription}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })}

                {!filteredCompanies.length ? (
                  <div className="rsa-company-empty">
                    No companies match your search.
                  </div>
                ) : null}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};



/**
 * Inline, compact checkbox list used inside the PORTFOLIO option card.
 * No borders/card container around items (checkbox + text only).
 * Now shows ALL companies (no truncation).
 */
const CompactCompanyCheckboxes = ({
  companies,
  selectedIds,
  onToggle,
  previewCount = 9999,
}) => {
  if (!Array.isArray(companies) || !companies.length) return null;

  const show = companies.slice(0, previewCount);

  return (
    <div className="rsa-compact-company-list">
      {show.map((c) => {
        const checked = selectedIds.includes(c.companyId);
        return (
          <label
            key={c.companyId}
            className="rsa-compact-company-row"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="rsa-company-checkbox">
              <input
                type="checkbox"
                checked={checked}
                onChange={() => onToggle(c.companyId)}
              />
              <span className="rsa-company-checkbox-ui">
                <img
                  src={process.env.PUBLIC_URL + "/images/icons/tick-white.svg"}
                  alt="icon"
                />
              </span>
            </div>

            <div className="rsa-compact-company-text">
              {c.companyWebsite ? (
                <a
                  className="rsa-company-title-link"
                  href={c.companyWebsite}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className="rsa-company-name">{c.companyName}</span>
                </a>
              ) : (
                <span className="rsa-company-name">{c.companyName}</span>
              )}

              {c.companySummary ? (
                <span className="rsa-company-summary-inline">
                  <span className="rsa-company-summary-sep"> — </span>
                  <span>{c.companySummary}</span>
                </span>
              ) : null}
            </div>
          </label>
        );
      })}
    </div>
  );
};

const RequestEventOption = (props) => {
  const [ref, inView] = useInView({ triggerOnce: true });
  const [checked, setChecked] = useState(false);

  const {
    OPTION_MEET_PORTFOLIO,
    OPTION_WELCOME_LUNCH,
    OPTION_WELCOME_DRINKS,
    OPTION_SUMMIT,
  } = props.optionIds || {};

  const optionId = props.itemData?.optionId;

  // Keep checkbox state in sync with form.options
  useEffect(() => {
    const selectedOpts = Array.isArray(props.form?.options)
      ? props.form.options
      : [];
    const isInForm = selectedOpts.includes(optionId);

    if (optionId === OPTION_MEET_PORTFOLIO) {
      // Portfolio checkbox is checkable even with zero companies
      setChecked(isInForm || props.form?.selectedOption === OPTION_MEET_PORTFOLIO);
      return;
    }

    setChecked(isInForm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.form?.options, props.form?.selectedOption, optionId]);

  const onChange = (e) => {
    const nextChecked = e.target.checked;

    // Portfolio checkbox: independent selection, optionally open companies list when checking
    if (optionId === OPTION_MEET_PORTFOLIO) {
      const opts = Array.isArray(props.form.options) ? [...props.form.options] : [];
      const has = opts.includes(optionId);

      if (nextChecked) {
        if (!has) opts.push(optionId);

        props.updateForm({
          options: opts,
          selectedOption: OPTION_MEET_PORTFOLIO,
        });

        if (typeof props.onPortfolioCheckboxOpenCompanies === "function") {
          props.onPortfolioCheckboxOpenCompanies();
        }
      } else {
        // Uncheck: clear selected companies and remove option + selectedOption
        const adjustedArr = (props.form.options || []).filter((id) => id !== optionId);
        props.updateForm({
          options: adjustedArr,
          // companyOptions: [], // do NOT auto-clear selected companies on uncheck
          selectedOption: null,
        });
      }

      setChecked(nextChecked);
      return;
    }

    setChecked(nextChecked);

    if (nextChecked) {
      const arr = Array.isArray(props.form.options) ? [...props.form.options] : [];
      arr.push(optionId);
      props.updateForm({ options: arr });
    } else {
      const adjustedArr = (props.form.options || []).filter((id) => id !== optionId);
      props.updateForm({ options: adjustedArr });
    }
  };

  const redesignMode = true;
  const checkboxMode = props.checkboxMode || false;
  const randomId = randomString(6);

  if (props.itemData.hidden) return null;

  // Bottom form checkbox rows
  if (checkboxMode) {
    // Portfolio special row w/ time range
    if (optionId === OPTION_MEET_PORTFOLIO) {
      const isPortfolioChecked =
        (Array.isArray(props.form.options) &&
          props.form.options.includes(OPTION_MEET_PORTFOLIO)) ||
        props.form.selectedOption === OPTION_MEET_PORTFOLIO;

      return (
        <label htmlFor={randomId} className="check">
          <div className="check__box">
            <input
              type="checkbox"
              id={randomId}
              checked={isPortfolioChecked}
              onChange={onChange}
            />
            <span>
              <img
                src={process.env.PUBLIC_URL + "/images/icons/tick-white.svg"}
                alt="icon"
              />
            </span>
          </div>

          <div className="check-label">
            {/* keep your original separator style */}
            {`Mon 23 Mar – Thu 26 Mar | `}
            <span className="hide-mob">&nbsp;</span>
            <div className="show-mob" style={{ width: "100%", height: "8px" }} />
            <span className="show-mob">&nbsp;</span>
            {`Meet our portfolio companies`}
          </div>
        </label>
      );
    }

    function removeLastSegment(inputString) {
      if (typeof inputString !== "string" || inputString.trim() === "") return "";
      const segments = inputString.split(/\s+/);
      if (segments.length <= 1) return "";
      const result = segments.slice(0, -1).join(" ");
      return result.trim();
    }

    return (
      <label htmlFor={randomId} className="check">
        <div className="check__box">
          <input
            type="checkbox"
            id={randomId}
            checked={checked}
            onChange={onChange}
          />
          <span>
            <img
              src={process.env.PUBLIC_URL + "/images/icons/tick-white.svg"}
              alt="icon"
            />
          </span>
        </div>

        <div className="check-label">
          {`${formatDatestring(props.itemData.dateString, {
            shortDay: true,
          })} | ${removeLastSegment(
            (props.itemData.timeString || "").replace(" - 12:00pm", "am - 12:00pm")
          )}`}
          <span className="hide-mob">&nbsp;|&nbsp;</span>
          <div className="show-mob" style={{ width: "100%", height: "8px" }} />
          <span className="show-mob">&nbsp;</span>
          {props.itemData.title}
        </div>
      </label>
    );
  }

  // Top redesigned option cards
  if (redesignMode) {
    const headerBg = `${process.env.PUBLIC_URL}/images/deal/option-header-bg.jpg`;

    let [titlePrefix, title] = (props.itemData.title || "").split(":");
    if (!title) {
      title = titlePrefix;
      titlePrefix = null;
    }
    const shortTitle = props.itemData.shortTitle;

    // Company selection helpers for portfolio option card
    const allCompanies = Array.isArray(props.event?.companyOptions)
      ? props.event.companyOptions
      : [];
    const selectedCompanyIds = Array.isArray(props.form?.companyOptions)
      ? props.form.companyOptions
      : [];

    const ensurePortfolioOptionOn = (nextSelected) => {
      const hasAny = nextSelected.length > 0;
      const opts = Array.isArray(props.form?.options) ? props.form.options : [];
      const hasPortfolio = opts.includes(OPTION_MEET_PORTFOLIO);

      if (hasAny && !hasPortfolio) {
        props.updateForm({ options: [...opts, OPTION_MEET_PORTFOLIO] });
      }
      // Do NOT auto-remove here; user may have explicitly checked the option
    };

    const toggleCompanyInline = (companyId) => {
      const isSelected = selectedCompanyIds.includes(companyId);
      const next = isSelected
        ? selectedCompanyIds.filter((id) => id !== companyId)
        : [...selectedCompanyIds, companyId];

      props.updateForm({ companyOptions: next });
      ensurePortfolioOptionOn(next);
    };

    const isPortfolioCard = optionId === OPTION_MEET_PORTFOLIO;

    const displayDate = isPortfolioCard
      ? "Mon, March 23 – Thu, March 26"
      : (props.itemData.dateString || "").replace(", 2026", "");

    const displayTime = isPortfolioCard
      ? ""
      : (props.itemData.timeString || "")
          .replace(" - 12:00pm", "am - 12:00pm")
          .replace("pm PT", " PM")
          .replace("am PT", " AM");

    return (
      <motion.div
        ref={ref}
        initial={{ opacity: 0, x: 20, y: 0 }}
        animate={inView ? { opacity: 1, y: 0, x: 0 } : {}}
        transition={{ duration: 0.9, ease: "easeOut" }}
      >
        <div className="option-container" id={`option-${optionId}`}>
          <div
            className="option-header logo"
            style={{ backgroundImage: `url(${headerBg})` }}
          >
            {optionId === OPTION_MEET_PORTFOLIO && (
              <div className="top mt">
                <div className="text prefix">Landmark Ventures</div>
                <div className="text">
                  {`MEET OUR PORTFOLIO COMPANIES`.toUpperCase()}
                </div>
                <div className="text postfix">RSA Week | San Francisco</div>
              </div>
            )}

            {optionId === OPTION_WELCOME_LUNCH && (
              <div className="top mt">
                <div className="text prefix">Landmark Ventures</div>
                <div className="text">
                  {`VIP CISO WELCOME LUNCH`.toUpperCase()}
                </div>
                <div className="text postfix">RSA Week | San Francisco</div>
              </div>
            )}

            {optionId === OPTION_WELCOME_DRINKS && (
              <div className="top mt">
                <div className="text prefix">Landmark Ventures</div>
                <div className="text">{`VIP COCKTAIL PARTY`.toUpperCase()}</div>
                <div className="text postfix">RSA Week | San Francisco</div>
              </div>
            )}

            {optionId === OPTION_SUMMIT && (
              <div className="top mt">
                <div className="text prefix">
                  Israel Export Institute &amp; Foreign Trade Administration
                </div>
                <div className="text">
                  {`GLOBAL-ISRAEL INNOVATION SUMMIT`.toUpperCase()}
                </div>
                <div className="text postfix">Held in parallel with RSA</div>
              </div>
            )}

            {optionId !== OPTION_MEET_PORTFOLIO &&
              optionId !== OPTION_WELCOME_LUNCH &&
              optionId !== OPTION_WELCOME_DRINKS &&
              optionId !== OPTION_SUMMIT && (
                <div className="top mt">
                  {titlePrefix ? (
                    <div className="text prefix">{titlePrefix}</div>
                  ) : null}
                  <div className="text">{(title || "").toUpperCase()}</div>
                  {shortTitle ? (
                    <div className="text postfix">{shortTitle}</div>
                  ) : null}
                </div>
              )}
          </div>

          <div className="option-body">
            <div className="upper-container">
              <div className="date-time-container">
                <div className="date">{displayDate}</div>
                <div className="date">{displayTime}</div>
              </div>

              <div className="date-time-container text-center">
                {props.itemData.subLocation ? (
                  <div className="date location">{props.itemData.subLocation}</div>
                ) : null}
                <div className="date location">Hotel Nikko San Francisco</div>
              </div>
            </div>

            {optionId === OPTION_MEET_PORTFOLIO && (
              <div className="description">
                <p>
                  Landmark Ventures is facilitating 1:1 meetings during RSA week
                  with a curated set of portfolio companies across the cyber
                  ecosystem. Select the companies you’d like to meet, and our team
                  will do our best to coordinate introductions.
                </p>

                {/* show ALL companies (no truncation) */}
                <CompactCompanyCheckboxes
                  companies={allCompanies}
                  selectedIds={selectedCompanyIds}
                  onToggle={toggleCompanyInline}
                  previewCount={9999}
                />

                {/* Prefer-to-search text removed (kept for later) */}
                {/* {allCompanies.length > 12 ? (
                  <p style={{ marginTop: 10 }}>
                    Prefer to search and view the full list?{" "}
                    <a
                      className="rsa-company-title-link"
                      href="#apply"
                      onClick={(e) => {
                        e.preventDefault();
                        if (typeof props.onRequestCompanies === "function") {
                          props.onRequestCompanies();
                        }
                      }}
                    >
                      View all companies
                    </a>
                  </p>
                ) : null} */}
              </div>
            )}

            {optionId === OPTION_WELCOME_LUNCH && (
              <div className="description">
                <p>
                  Welcome to RSA Week in San Francisco. Landmark Ventures is
                  hosting a private, invite-only lunch for senior security and
                  technology leaders in town for the conference.
                </p>
                <p>
                  This is designed to be a high-signal gathering — an opportunity
                  to connect with peer CISOs and CIOs away from the show floor,
                  compare notes on what’s driving priorities in 2026, and build
                  relationships with the broader enterprise cyber ecosystem.
                </p>
                <p>
                  <strong>What to expect:</strong>
                </p>
                <ul>
                  <li>
                    <span className="bullet">•</span> Curated peer group (limited
                    capacity)
                  </li>
                  <li>
                    <span className="bullet">•</span> CISO / CIO-level networking
                    in a private setting
                  </li>
                  <li>
                    <span className="bullet">•</span> Informal conversation over
                    lunch — no panels, no pitches
                  </li>
                </ul>
              </div>
            )}

            {optionId === OPTION_WELCOME_DRINKS && (
              <div className="description">
                <p>
                  Join Landmark Ventures for an invite-only cocktail reception
                  during RSA Week. This is a relaxed, high-value networking
                  environment for senior leaders across enterprise security,
                  infrastructure, and innovation.
                </p>
                <p>
                  Whether you’re looking to reconnect with peers, meet new
                  operators, or expand your network across the global cyber
                  ecosystem, this session is built for high-quality conversations
                  in a standout setting.
                </p>
                <p>
                  <strong>Event highlights:</strong>
                </p>
                <ul>
                  <li>
                    <span className="bullet">•</span> VIP networking with
                    decision-makers attending RSA
                  </li>
                  <li>
                    <span className="bullet">•</span> Executive-friendly format
                    (easy to drop in)
                  </li>
                  <li>
                    <span className="bullet">•</span> Curated guest list and
                    meaningful introductions
                  </li>
                </ul>
              </div>
            )}

            {optionId === OPTION_SUMMIT && (
              <div className="description">
                <p>
                  Join us on March 23rd for an exclusive, high-impact business
                  event hosted by the Israel Export Institute and the Foreign
                  Trade Administration at the Ministry of Economy and Industry.
                  Held in parallel with the RSA Conference, this summit brings
                  together top innovators and the global enterprise cyber
                  ecosystem for a day of networking and business growth.
                </p>

                <p>
                  This special VIP Event (invitation-only; complimentary) brings
                  together an exclusive group of CISOs, CIOs, CEOs, VC Funds and
                  leading decision-makers from around the world.
                </p>

                <p>
                  <strong>Event Highlights Include:</strong>
                </p>
                <ul>
                  <li>
                    <span className="bullet">•</span> TED-style innovation stage
                  </li>
                  <li>
                    <span className="bullet">•</span> High-value networking
                    sessions
                  </li>
                  <li>
                    <span className="bullet">•</span> Direct access to the global
                    enterprise cyber community
                  </li>
                  <li>
                    <span className="bullet">•</span> Investor &amp; VC showcase
                  </li>
                  <li>
                    <span className="bullet">•</span> Pre-scheduled B2B meetings
                  </li>
                </ul>
                <p className="more-info">
                For more information, visit{" "}
                <a
                  href="https://globalisraelinnovationsummit.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  globalisraelinnovationsummit.com
                </a>
                .
              </p>

              </div>
            )}
          </div>
        </div>
      </motion.div>
    );
  }

  return null;
};
